import autobind from "autobind-decorator";
import { observer } from "mobx-react";
import React from "react";
import ReactDOM from "react-dom";
import styled, { css } from "styled-components";

import Style from "@client/utils/Style";

import DropdownButton from "../DropdownButton";
import { List, ListItems, Tip } from "../DropdownLink";

const Wrapper = styled.div`
  position: relative;
`;

const ListItem = styled.div<{ disabled?: boolean; color?: string }>`
  color: ${props =>
    props.disabled
      ? Style.palette.grayscale[2]
      : props.color || Style.palette.black};
  height: 32px;
  padding: 0 16px;
  cursor: ${(props: any) => (props.disabled ? "default" : "pointer")};
  display: flex;
  align-items: center;
  transition: all ${Style.animations.swift};
  &:hover {
    ${props =>
      props.disabled
        ? ""
        : css`
            background: ${Style.palette.grayscale[0]};
          `}
  }
  &:first-child {
    border-top-left-radius: ${Style.borderRadius};
    border-top-right-radius: ${Style.borderRadius};
  }
  &:last-child {
    border-bottom-left-radius: ${Style.borderRadius};
    border-bottom-right-radius: ${Style.borderRadius};
  }
`;
const ListStyle = css`
  box-shadow: 0 0 8px 0px rgba(111, 114, 118, 0.51);
  border: none;
`;
export type Action = {
  label: string;
  color?: string;
  action: () => void;
  disabled?: boolean;
  hidden?: boolean;
};
export type Props = {
  label?: string;
  actions: Action[];
  style?: any;
};

type State = {
  showDropdownList: boolean;
};

@observer
class DropdownAction extends React.Component<Props, State> {
  static defaultProps: Partial<Props> = {
    label: "Actions",
  };

  state: State = {
    showDropdownList: false,
  };

  itemMouseDown = false;
  listRef: HTMLElement | null = null;
  tipRef: HTMLElement | null = null;
  buttonRef: HTMLElement | null = null;
  buttonRect: ClientRect | null = null;

  componentDidMount() {
    window.addEventListener("mousedown", this.handlePageClick, false);
    if (this.buttonRef) {
      this.buttonRect = this.buttonRef.getBoundingClientRect();
    }
  }

  UNSAFE_componentWillUpdate() {
    if (this.buttonRef) {
      this.buttonRect = this.buttonRef.getBoundingClientRect();
    }
  }

  componentDidUpdate() {
    this.updateListPosition();
  }

  componentWillUnmount() {
    window.removeEventListener("mousedown", this.handlePageClick, false);
  }

  updateListPosition() {
    if (
      !this.state.showDropdownList ||
      !this.listRef ||
      !this.tipRef ||
      !this.buttonRef ||
      !this.buttonRect
    ) {
      return;
    }
    const tipHeight = this.tipRef.offsetHeight / 2;
    const topOffset = 6;

    // If a modal is opened, body scroll is removed and body top is set to replicate scroll position
    let scrollOffset = 0;
    if (document.body && parseInt(document.body.style.top, 10) < 0) {
      scrollOffset = -parseInt(document.body && document.body.style.top, 10);
    }

    this.listRef.style.top = `${
      this.buttonRect.top +
      this.buttonRect.height +
      tipHeight +
      topOffset +
      (window.pageYOffset || scrollOffset)
    }px`;
    this.listRef.style.left = `${this.buttonRect.left + window.pageXOffset}px`;
  }

  @autobind
  handlePageClick() {
    if (!this.itemMouseDown) {
      this.setState({ showDropdownList: false });
    }
  }

  handleButtonClick() {
    this.setState({ showDropdownList: !this.state.showDropdownList });
  }

  handleItemMouseHover(action: Action, index: number, isEnter: boolean) {
    if (!this.tipRef || index !== 0 || action.disabled) {
      return;
    }
    this.tipRef.style.background = isEnter
      ? Style.palette.grayscale[0]
      : Style.palette.grayscale[1];
  }

  handleItemClick(action: Action) {
    if (action.disabled) {
      return;
    }
    action.action();
    this.setState({ showDropdownList: false });
  }

  renderListItems() {
    return (
      <ListItems>
        {this.props.actions
          .filter(a => !a.hidden)
          .map((action, i) => (
            <ListItem
              onMouseEnter={() => {
                this.handleItemMouseHover(action, i, true);
              }}
              onMouseLeave={() => {
                this.handleItemMouseHover(action, i, false);
              }}
              onMouseDown={() => {
                this.itemMouseDown = true;
              }}
              onMouseUp={() => {
                this.itemMouseDown = false;
              }}
              key={action.label}
              onClick={() => {
                this.handleItemClick(action);
              }}
              color={action.color}
              disabled={action.disabled}
            >
              {action.label}
            </ListItem>
          ))}
      </ListItems>
    );
  }

  renderList() {
    if (!this.state.showDropdownList) {
      return null;
    }

    const body: any = document.body;
    return ReactDOM.createPortal(
      <List
        ref={list => {
          this.listRef = list;
        }}
        width={`${Style.inputSizes.regular.width}px`}
        padding={0}
        customStyle={ListStyle}
      >
        <Tip
          ref={ref => {
            this.tipRef = ref;
          }}
          borderColor="rgba(111, 114, 118, 0.2)"
        />
        {this.renderListItems()}
      </List>,
      body,
    );
  }

  render() {
    return (
      <Wrapper style={this.props.style}>
        <DropdownButton
          secondary
          centered
          value={this.props.label || ""}
          customRef={ref => {
            this.buttonRef = ref;
          }}
          onClick={() => {
            this.handleButtonClick();
          }}
        />
        {this.renderList()}
      </Wrapper>
    );
  }
}

export default DropdownAction;
